import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import DownloadIcon from '@mui/icons-material/Download';
import { makeStyles } from '@mui/styles';

import {
  BiannualAppointmentStatus,
  BiannualStatus,
  EstablishedVisitStatus,
  Language,
  MemberStatus,
} from '@vestahealthcare/common/enums';
import Enum, { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  BaseEnum,
  Employee,
  EmployeeGroup,
  MemberWarningType,
  Organization,
  PaginationType,
  PodConfig,
  ProgramExtension,
  ProgramExtensionStatus,
  VisitSummaryItem,
} from '@vestahealthcare/common/models';
import {
  DATE_FORMAT,
  LS_CLINICAL_VISIT_SUMMARY_V2,
} from '@vestahealthcare/common/utils/constants';

import {
  FilterItem,
  IconButton,
  Panel,
  Select,
  UNASSIGNED,
} from 'styleguide-v2';

import { showGlobalError } from 'dash/src/components/GlobalMessage';
import { CacheServices } from 'dash/src/services';
import {
  GetVisitSummaryParams,
  downloadVisitSummaryExport,
  fetchVisitSummaryItems,
} from 'dash/src/services/VisitServices';
import {
  LoadFilterOption,
  getServerFilters,
  loadNewFiltersFromStorage,
  saveNewFiltersToStorage,
} from 'dash/src/utils/filterUtils';
import { useQueryParams } from 'dash/src/utils/useQueryParams';

import { dataUtils } from '../../../utils/dataUtils';
import { POD_REPORTING_IS_UNREACHABLE_OPTIONS } from '../PodReporting/PodReportingFiltersBar';
import { VisitSummaryFilterBar } from './VisitSummaryFilterBar';
import { VisitSummaryTable } from './VisitSummaryTable';

const DEFAULT_PAGE_SIZE = 25;
const NUMBER_OF_PAST_MONTHS = 12;
const NUMBER_OF_FUTURE_MONTHS = 6;

type KeyGetVisitSummaryParams = keyof GetVisitSummaryParams;

const useStyles = makeStyles({
  date: {
    width: '15rem',
  },
});

let visitSummaryFiltersString = '';

const DEFAULT_SORT = 'fullName asc';

export const VisitsSummaryPage = () => {
  const history = useHistory();
  const query = useQueryParams();
  const styles = useStyles();

  const [employees, setEmployees] = useState<Employee[]>();
  const [languages, setLanguages] = useState<Language[]>();
  const [podConfigs, setPodConfigs] = useState<PodConfig[]>();
  const [referralSources, setReferralSources] = useState<Organization[]>();
  const [programExtensions, setProgramExtensions] = useState<
    ProgramExtension[]
  >([]);
  const [programExtensionStatuses, setProgramExtensionStatuses] = useState<
    ProgramExtensionStatus[]
  >([]);
  const [warningTypes, setWarningTypes] = useState<MemberWarningType[]>([]);
  const [appointmentTypes, setAppointmentTypes] = useState<string[]>([]);
  const [worklistGroups, setWorklistGroups] = useState<EmployeeGroup[]>([]);

  const [filters, setFilters] = useState<
    {
      [x in KeyGetVisitSummaryParams]?: FilterItem;
    }
  >();
  const [sort, setSort] = useState<string>(DEFAULT_SORT);

  const [visits, setVisits] = useState<VisitSummaryItem[]>([]);
  const [pagination, setPagination] = useState<PaginationType>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingFilters, setLoadingFilters] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  const pastMonths = [] as Moment[];
  const futureMonths = [] as Moment[];

  pastMonths.push(moment());
  for (let i = NUMBER_OF_PAST_MONTHS - 1; i > 0; i -= 1) {
    pastMonths.push(
      moment()
        .subtract(NUMBER_OF_PAST_MONTHS - i, 'month')
        .endOf('month'),
    );
  }

  for (let i = NUMBER_OF_FUTURE_MONTHS - 1; i > 0; i -= 1) {
    futureMonths.push(moment().add(i, 'month').endOf('month'));
  }

  const [month, setMonth] = useState<Moment | undefined>(moment());

  const filtersData = useMemo(
    () => ({
      appointmentTypes: BaseEnum.toSelectable(
        appointmentTypes.map((a) => new BaseEnum({ id: a, description: a })),
      ),
      employees: employees
        ? [
            ...Employee.toSelectable([UNASSIGNED]),
            ...Employee.toSelectable(employees),
          ]
        : undefined,

      languages: languages
        ? Language.toSelectable(languages).filter(({ disabled }) => !disabled)
        : undefined,
      referralSources: referralSources
        ? Organization.toSelectable(referralSources)
        : undefined,
      podConfigs: podConfigs
        ? [
            { value: 0, label: translate('global.unassigned') } as Selectable,
            ...PodConfig.toSelectable(podConfigs),
          ]
        : undefined,
      programExtensions: ProgramExtension.toSelectable(programExtensions),
      status: Enum.toSelectable(
        MemberStatus.asArray.filter((item) => item !== MemberStatus.INVALID),
      ),
      warningTypes: MemberWarningType.toSelectable(warningTypes),
      worklistGroups: EmployeeGroup.toSelectable(
        worklistGroups.filter(({ id }) => id !== -1),
      ),
    }),
    [
      appointmentTypes,
      employees,
      languages,
      podConfigs,
      programExtensions,
      programExtensionStatuses,
      referralSources,
      warningTypes,
      worklistGroups,
    ],
  );

  const storedFiltersConfiguration = useMemo(() => {
    if (
      filtersData?.appointmentTypes?.length &&
      filtersData?.employees?.length &&
      filtersData?.languages?.length &&
      filtersData?.podConfigs?.length &&
      filtersData?.referralSources?.length &&
      filtersData?.programExtensions?.length &&
      filtersData?.warningTypes?.length &&
      filtersData?.worklistGroups?.length
    ) {
      return {
        biannualStatus: {
          data: Enum.toSelectable(BiannualStatus.asArray),
          label: translate('visitsSummary.filter.biannualStatus'),
        },
        biannualAppointmentStatus: {
          data: Enum.toSelectable(BiannualAppointmentStatus.asArray),
          label: translate('visitsSummary.filter.biannualAppointmentStatus'),
        },
        careCoordinatorId: {
          data: filtersData.employees,
          label: translate('components.FiltersVestaTeam.clinicalAssistant'),
          multiple: true,
        },
        ccmMinutesFrom: {
          label: translate('visitsSummary.filter.ccmMinutesFrom'),
        },
        ccmMinutesTo: {
          label: translate('visitsSummary.filter.ccmMinutesTo'),
        },
        engagementOwnerId: {
          data: filtersData.employees,
          label: translate('components.FiltersVestaTeam.engagementOwner'),
          multiple: true,
        },
        establishedVisitStatus: {
          data: Enum.toSelectable(EstablishedVisitStatus.asArray),
          label: translate('visitsSummary.filter.establishedVisitStatus'),
        },
        establishedVisitAppointmentStatus: {
          data: Enum.toSelectable(BiannualAppointmentStatus.asArray),
          label: translate(
            'visitsSummary.filter.establishedVisitAppointmentStatus',
          ),
        },
        excludedProgramExtensionId: {
          data: filtersData.programExtensions,
          label: translate('visitsSummary.filter.excludedProgramExtensions'),
          multiple: true,
        },
        excludeAppointmentTypesInPriorMonth: {
          data: filtersData.appointmentTypes,
          label: translate(
            'visitsSummary.filter.excludeAppointmentTypesInPriorMonth',
          ),
          multiple: true,
        },
        healthCoachId: {
          data: filtersData.employees,
          label: translate('components.FiltersVestaTeam.healthCoach'),
          multiple: true,
        },
        isComplex: {},
        isUnreachable: {
          data: POD_REPORTING_IS_UNREACHABLE_OPTIONS,
          label: translate('visitsSummary.filter.unreachable'),
        },
        language: {
          data: filtersData.languages,
          label: translate('visitsSummary.filter.language'),
          multiple: true,
        },
        npOwnerId: {
          data: filtersData.employees,
          label: translate('components.FiltersVestaTeam.npOwner'),
          multiple: true,
        },
        podId: {
          data: filtersData.podConfigs,
          label: translate('visitsSummary.filter.pod'),
          multiple: true,
        },
        podManagerId: {
          data: filtersData.employees,
          label: translate('components.FiltersVestaTeam.podManager'),
          multiple: true,
        },
        programExtensionFilter: {
          label: translate('visitsSummary.filter.programExtensions'),
          loader: dataUtils.programExtensions.loader(
            programExtensions,
            programExtensionStatuses,
          ),
          valueLabel: dataUtils.programExtensions.labeler,
          multiple: true,
        },
        referralSource: {
          data: filtersData.referralSources,
          label: translate('visitsSummary.filter.referralSource'),
          multiple: true,
        },
        rnOwnerId: {
          data: filtersData.employees,
          label: translate('components.FiltersVestaTeam.rnOwner'),
          multiple: true,
        },
        status: {
          data: Enum.toSelectable(
            MemberStatus.asArray.filter(
              (item) => item !== MemberStatus.INVALID,
            ),
          ),
          label: translate('visitsSummary.filter.status'),
          multiple: true,
        },
        warningTypeId: {
          data: MemberWarningType.toSelectable(warningTypes),
          label: translate('components.MemberWarningDropdown.label'),
        },
        worklistGroupId: {
          data: filtersData.worklistGroups,
          label: translate('visitsSummary.filter.groups'),
          multiple: true,
        },
      } as { [x in KeyGetVisitSummaryParams]: LoadFilterOption };
    }
  }, [filtersData]);

  useEffect(() => {
    if (storedFiltersConfiguration) {
      const storedFilters = loadNewFiltersFromStorage(
        query.get('clearFilters') ? '' : LS_CLINICAL_VISIT_SUMMARY_V2,
        storedFiltersConfiguration,
        query,
      );
      const month = query.get('month');
      const monthParsed = moment(month, DATE_FORMAT);
      if (month && monthParsed?.isValid()) {
        setMonth(monthParsed);
      }
      setFilters({
        ...storedFilters,
      });
      history.replace('?');
    }
  }, [storedFiltersConfiguration]);

  useEffect(() => {
    if (filters) {
      saveNewFiltersToStorage(LS_CLINICAL_VISIT_SUMMARY_V2, filters);
    }
  }, [filters]);

  const getInitialData = async () => {
    setLoadingFilters(true);
    const [
      worklistGroups,
      employees,
      pes,
      pess,
      languages,
      pods,
      organizations,
      warningTypes,
      appointmentTypes,
    ] = await Promise.all([
      CacheServices.getEmployeeGroupsWorklist(),
      CacheServices.getEmployees(),
      CacheServices.getAllProgramExtensions(),
      CacheServices.getProgramExtensionStatus(),
      CacheServices.getLanguages(),
      CacheServices.getPodConfigs(),
      CacheServices.getOrganizations(),
      CacheServices.getAvailableWarnings(),
      CacheServices.getAppointmentTypes(),
    ]);

    setEmployees(employees);
    setLanguages(languages);
    setReferralSources(organizations);
    setPodConfigs(pods);
    setProgramExtensions(pes);
    setProgramExtensionStatuses(pess);
    setWorklistGroups(worklistGroups);
    setWarningTypes(warningTypes);
    setAppointmentTypes(appointmentTypes);
    setLoadingFilters(false);
  };

  const getData = async () => {
    const offset = page * pageSize;
    if (!filters) return;
    if (offset !== 0 && offset < visits?.length) return;

    const currentFilters = getServerFilters(filters, {
      programExtensionFilter: dataUtils.programExtensions.parser,
    });

    currentFilters.isUnreachable =
      currentFilters.isUnreachable === 'null'
        ? undefined
        : currentFilters.isUnreachable;

    setLoading(true);
    try {
      visitSummaryFiltersString = JSON.stringify(currentFilters);
      const { items, pagination } = await fetchVisitSummaryItems({
        ...currentFilters,

        sort,
        month,
        offset,
        limit: pageSize,
      });

      if (visitSummaryFiltersString === JSON.stringify(currentFilters)) {
        if (page > 0) {
          setVisits([...visits, ...items]);
        } else {
          setVisits(items);
        }
        setPagination(pagination);
      }
    } catch (e) {
      showGlobalError(e as string);
    }
    setLoading(false);
  };

  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    getData();
  }, [page, pageSize]);

  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    } else {
      getData();
    }
  }, [filters, month, sort]);

  return (
    <Panel>
      <Panel.Heading title={translate('visitsSummary.title')} filtersV2>
        <Panel.Actions>
          <Select
            className={styles.date}
            data-cy="pod-reporting-filters-month"
            disableClearable
            getItemLabel={(date: Moment) =>
              date.format('MMM YYYY').toLocaleUpperCase()
            }
            items={[...futureMonths, ...pastMonths]}
            isOptionEqualToValue={(a: Moment, b: Moment) => a.isSame(b, 'M')}
            onChange={(month?: Moment) => {
              setMonth(month);
            }}
            placeholder={translate('visitsSummary.filter.month')}
            value={month}
          />
          <IconButton
            data-cy="member-export"
            tooltip={translate('global.download')}
            loading={downloadLoading}
            onClick={async () => {
              if (!filters) return;
              setDownloadLoading(true);
              const currentFilters = getServerFilters(filters, {
                programExtensionFilter: dataUtils.programExtensions.parser,
              });

              await downloadVisitSummaryExport(currentFilters);
              setDownloadLoading(false);
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Panel.Actions>
        <Panel.FilterBar
          chips={filters}
          inputs={
            <VisitSummaryFilterBar
              data={{
                appointmentTypes: filtersData.appointmentTypes || [],
                employees: filtersData.employees || [],
                languages: filtersData.languages || [],
                pods: filtersData.podConfigs || [],
                referralSources: filtersData.referralSources || [],
              }}
              filters={filters}
              loading={loadingFilters}
              onChange={(filters) => {
                setPage(0);
                setFilters(filters);
              }}
            />
          }
          onClearFilters={() => {
            setFilters({});
          }}
          onDeleteFilter={(key: string) => {
            setFilters({
              ...filters,
              [key]: undefined,
            });
          }}
        />
      </Panel.Heading>
      <Panel.Body loading={loading} fixedTableHeader>
        <VisitSummaryTable
          defaultSort={sort}
          items={visits}
          onChangePage={setPage}
          onChangePageSize={(ps) => {
            setPage(0);
            setPageSize(ps);
          }}
          onDefaultSort={() => setSort(DEFAULT_SORT)}
          onSort={(field, direction) => setSort(`${field} ${direction}`)}
          pagination={pagination}
        />
      </Panel.Body>
    </Panel>
  );
};

export default VisitsSummaryPage;
